// ============================================
// Patient-Facing Supplement Catalog
// Grid listing of the 74-product formulary
// Filters: category (subcategory), timing, search
// ============================================

import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getAllProducts } from '../../services/firestore/formularyService';
import { getPatientProtocolSupplements } from '../../services/firestore/analysisService';
import { onPatientAuthStateChanged } from '../../services/auth/authUpgradeService';
import { useApp } from '../../context/AppContext';
import type { Product, ProductSubcategory } from '../../types/formulary';
import type { ProtocolTiming } from '../../types/index';

// ── Subcategory display labels ──

const SUBCATEGORY_LABELS: Record<string, string> = {
  foundation: 'Foundation',
  beauty: 'Beauty',
  weight_metabolic: 'Weight & Metabolic',
  hormone_female: 'Hormone (Female)',
  hormone_male: 'Hormone (Male)',
  clinical: 'Clinical',
  hrt_female: 'HRT (Female)',
  hrt_male: 'HRT (Male)',
  thyroid: 'Thyroid',
  glp1_weight: 'GLP-1 Weight',
  skin_hair: 'Skin & Hair',
  mental_health: 'Mental Health',
  sexual_female: 'Sexual Health (Female)',
  sexual_male: 'Sexual Health (Male)',
  lipid: 'Lipid',
  nad_cellular: 'NAD+ & Cellular',
  gh_peptide: 'GH Peptide',
  mtor_longevity: 'mTOR & Longevity',
  metabolic_longevity: 'Metabolic Longevity',
  immune_inflammation: 'Immune & Inflammation',
  mitochondrial_cognitive: 'Mitochondrial & Cognitive',
  neurohormone: 'Neurohormone',
  hormone_precursor: 'Hormone Precursor',
  methylation_energy: 'Methylation & Energy',
  antioxidant_detox: 'Antioxidant & Detox',
  joint_musculoskeletal: 'Joint & Musculoskeletal',
  skin_regeneration: 'Skin Regeneration',
};

const TIMING_LABELS: Record<ProtocolTiming, string> = {
  AM_ENERGY: 'Morning',
  MIDDAY_ABSORPTION: 'Midday',
  PM_RECOVERY: 'Evening',
};

const TIMING_COLORS: Record<ProtocolTiming, string> = {
  AM_ENERGY: 'bg-amber-50 text-amber-700 border-amber-200',
  MIDDAY_ABSORPTION: 'bg-sky-50 text-sky-700 border-sky-200',
  PM_RECOVERY: 'bg-indigo-50 text-indigo-700 border-indigo-200',
};

const EVIDENCE_COLORS: Record<string, string> = {
  strong: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  moderate: 'bg-amber-50 text-amber-700 border-amber-200',
  emerging: 'bg-violet-50 text-violet-700 border-violet-200',
};

const CATEGORY_COLORS: Record<string, string> = {
  supplement: 'bg-teal-50 text-teal-700 border-teal-200',
  rx: 'bg-rose-50 text-rose-700 border-rose-200',
  longevity: 'bg-violet-50 text-violet-700 border-violet-200',
};

export function CatalogPage() {
  const navigate = useNavigate();
  const { state: appState } = useApp();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filterSubcategory, setFilterSubcategory] = useState<ProductSubcategory | 'all'>('all');
  const [filterTiming, setFilterTiming] = useState<ProtocolTiming | 'all'>('all');
  const [showFilters, setShowFilters] = useState(false);

  // Protocol supplements fetched from Firestore for returning authenticated patients
  const [savedSupplements, setSavedSupplements] = useState<{ name: string; productId?: string }[]>([]);

  // Load protocol from Firestore when patient is authenticated but has no session protocol
  useEffect(() => {
    const unsub = onPatientAuthStateChanged((authState) => {
      if (authState.isAuthenticated && authState.uid && !appState.protocol) {
        getPatientProtocolSupplements(authState.uid)
          .then((supplements) => {
            if (supplements) setSavedSupplements(supplements);
          })
          .catch(() => {
            // Non-critical — highlighting is an enhancement, not a gate
          });
      }
    });
    return unsub;
  }, [appState.protocol]);

  // Build a set of product IDs and names from the patient's active protocol
  // Prefers current session protocol; falls back to Firestore for returning patients
  const protocolProductIds = useMemo(() => {
    const ids = new Set<string>();
    const names = new Set<string>();
    const supplements = appState.protocol?.supplements ?? savedSupplements;
    for (const s of supplements) {
      if (s.productId) ids.add(s.productId);
      if (s.name) names.add(s.name.toLowerCase());
    }
    return { ids, names };
  }, [appState.protocol, savedSupplements]);

  useEffect(() => {
    async function load() {
      try {
        const data = await getAllProducts();
        // Only show active products to patients
        setProducts(data.filter((p) => p.status === 'active'));
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load catalog');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  // Derive available subcategories from loaded products
  const availableSubcategories = useMemo(() => {
    const subs = new Set(products.map((p) => p.subcategory));
    return Array.from(subs).sort((a, b) =>
      (SUBCATEGORY_LABELS[a] || a).localeCompare(SUBCATEGORY_LABELS[b] || b)
    );
  }, [products]);

  const filtered = useMemo(() => {
    return products.filter((p) => {
      if (search) {
        const q = search.toLowerCase();
        const nameMatch = p.name.toLowerCase().includes(q);
        const biomarkerMatch = p.targetBiomarkers?.some((b) => b.toLowerCase().includes(q));
        const notesMatch = p.clinicalNotes?.toLowerCase().includes(q);
        if (!nameMatch && !biomarkerMatch && !notesMatch) return false;
      }
      if (filterSubcategory !== 'all' && p.subcategory !== filterSubcategory) return false;
      if (filterTiming !== 'all') {
        const timingMatch = p.timing === filterTiming ||
          p.additionalTimings?.includes(filterTiming);
        if (!timingMatch) return false;
      }
      return true;
    });
  }, [products, search, filterSubcategory, filterTiming]);

  // ── Render ──

  return (
    <div style={{ backgroundColor: '#FAFAFA', minHeight: '100vh', fontFamily: "'DM Sans', system-ui, sans-serif" }}>
      {/* Nav */}
      <nav style={{
        position: 'sticky', top: 0, zIndex: 50,
        backgroundColor: 'rgba(250,250,250,0.85)', backdropFilter: 'blur(20px) saturate(180%)',
        borderBottom: '1px solid #E4E4E7',
        height: 64, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 24px',
      }}>
        <Link to="/landing" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
          <div style={{
            width: 30, height: 30, borderRadius: 3,
            background: 'linear-gradient(135deg, #E11D48, #FB7185)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 13, fontWeight: 700, color: 'white',
          }}>
            H
          </div>
          <span style={{ fontWeight: 700, fontSize: 15, letterSpacing: 1.8, color: '#0F172A' }}>HEMOLOGIX</span>
        </Link>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Link to="/landing" style={{
            fontSize: 13, fontWeight: 500, color: '#52525B', textDecoration: 'none',
            transition: 'color 0.2s',
          }}>
            Home
          </Link>
          <Link to="/landing" style={{
            padding: '10px 26px', fontSize: 13, fontWeight: 600,
            backgroundColor: '#18181B', color: 'white', border: 'none',
            borderRadius: 4, textDecoration: 'none',
            transition: 'all 0.25s',
          }}>
            Upload Labs
          </Link>
        </div>
      </nav>

      {/* Header */}
      <div style={{
        maxWidth: 1200, margin: '0 auto', padding: '48px 24px 0',
      }}>
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <div style={{
            fontSize: 12, fontWeight: 600, letterSpacing: 2.5,
            textTransform: 'uppercase', color: '#E11D48', marginBottom: 14,
          }}>
            SUPPLEMENT CATALOG
          </div>
          <h1 style={{
            fontFamily: "'Instrument Serif', Georgia, serif",
            fontSize: 'clamp(32px, 5vw, 48px)',
            fontWeight: 400, color: '#18181B', lineHeight: 1.15,
            margin: '0 0 16px',
          }}>
            Science-Backed Supplements
          </h1>
          <p style={{
            fontSize: 17, color: '#52525B', maxWidth: 640, margin: '0 auto',
            lineHeight: 1.6,
          }}>
            Our formulary of {products.length} targeted supplements, each mapped to specific
            biomarkers and backed by clinical evidence. Every recommendation starts with your labs.
          </p>
        </div>

        {/* Search & Filters */}
        <div style={{
          backgroundColor: '#FFFFFF', border: '1px solid #E4E4E7',
          borderRadius: 4, padding: 20, marginBottom: 24,
          boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
        }}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {/* Search */}
            <div style={{ flex: 1, minWidth: 240, position: 'relative' }}>
              <svg
                width="16" height="16" viewBox="0 0 24 24" fill="none"
                stroke="#A1A1AA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)' }}
              >
                <circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" />
              </svg>
              <input
                type="text"
                placeholder="Search supplements, biomarkers..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{
                  width: '100%', padding: '12px 14px 12px 40px',
                  fontSize: 15, fontFamily: "'DM Sans', sans-serif",
                  border: '1px solid #E4E4E7', borderRadius: 4,
                  backgroundColor: '#FAFAFA', color: '#18181B',
                  outline: 'none', transition: 'border-color 0.2s',
                }}
                onFocus={(e) => { e.target.style.borderColor = '#E11D48'; }}
                onBlur={(e) => { e.target.style.borderColor = '#E4E4E7'; }}
              />
            </div>

            {/* Filter toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '12px 20px', fontSize: 14, fontWeight: 500,
                fontFamily: "'DM Sans', sans-serif",
                border: `1.5px solid ${showFilters ? '#E11D48' : '#E4E4E7'}`,
                borderRadius: 4, cursor: 'pointer',
                backgroundColor: showFilters ? 'rgba(225,29,72,0.04)' : 'transparent',
                color: showFilters ? '#E11D48' : '#52525B',
                transition: 'all 0.2s',
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
              </svg>
              Filters
              {(filterSubcategory !== 'all' || filterTiming !== 'all') && (
                <span style={{
                  width: 18, height: 18, borderRadius: 9,
                  backgroundColor: '#E11D48', color: 'white',
                  fontSize: 11, fontWeight: 700,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {(filterSubcategory !== 'all' ? 1 : 0) + (filterTiming !== 'all' ? 1 : 0)}
                </span>
              )}
            </button>
          </div>

          {/* Filter dropdowns */}
          {showFilters && (
            <div style={{
              display: 'flex', gap: 12, marginTop: 16, paddingTop: 16,
              borderTop: '1px solid #E4E4E7', flexWrap: 'wrap',
            }}>
              <div>
                <label style={{
                  display: 'block', fontSize: 11, fontWeight: 600,
                  letterSpacing: 1.5, textTransform: 'uppercase',
                  color: '#A1A1AA', marginBottom: 6,
                }}>
                  Category
                </label>
                <select
                  value={filterSubcategory}
                  onChange={(e) => setFilterSubcategory(e.target.value as ProductSubcategory | 'all')}
                  style={{
                    padding: '10px 32px 10px 14px', fontSize: 14,
                    fontFamily: "'DM Sans', sans-serif",
                    border: '1px solid #E4E4E7', borderRadius: 4,
                    backgroundColor: '#FAFAFA', color: '#18181B',
                    cursor: 'pointer', outline: 'none',
                    appearance: 'none',
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23A1A1AA' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 10px center',
                  }}
                >
                  <option value="all">All Categories</option>
                  {availableSubcategories.map((sub) => (
                    <option key={sub} value={sub}>
                      {SUBCATEGORY_LABELS[sub] || sub}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{
                  display: 'block', fontSize: 11, fontWeight: 600,
                  letterSpacing: 1.5, textTransform: 'uppercase',
                  color: '#A1A1AA', marginBottom: 6,
                }}>
                  Timing
                </label>
                <select
                  value={filterTiming}
                  onChange={(e) => setFilterTiming(e.target.value as ProtocolTiming | 'all')}
                  style={{
                    padding: '10px 32px 10px 14px', fontSize: 14,
                    fontFamily: "'DM Sans', sans-serif",
                    border: '1px solid #E4E4E7', borderRadius: 4,
                    backgroundColor: '#FAFAFA', color: '#18181B',
                    cursor: 'pointer', outline: 'none',
                    appearance: 'none',
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23A1A1AA' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 10px center',
                  }}
                >
                  <option value="all">All Timings</option>
                  <option value="AM_ENERGY">Morning</option>
                  <option value="MIDDAY_ABSORPTION">Midday</option>
                  <option value="PM_RECOVERY">Evening</option>
                </select>
              </div>

              {(filterSubcategory !== 'all' || filterTiming !== 'all') && (
                <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <button
                    onClick={() => {
                      setFilterSubcategory('all');
                      setFilterTiming('all');
                    }}
                    style={{
                      padding: '10px 16px', fontSize: 13, fontWeight: 500,
                      fontFamily: "'DM Sans', sans-serif",
                      border: 'none', borderRadius: 4, cursor: 'pointer',
                      backgroundColor: 'rgba(225,29,72,0.06)', color: '#E11D48',
                      transition: 'background-color 0.2s',
                    }}
                  >
                    Clear filters
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Results count */}
        <div style={{
          fontSize: 14, color: '#A1A1AA', marginBottom: 20,
          fontFamily: "'JetBrains Mono', 'SF Mono', monospace",
        }}>
          {filtered.length} supplement{filtered.length !== 1 ? 's' : ''}
          {(search || filterSubcategory !== 'all' || filterTiming !== 'all') && ' matching'}
        </div>

        {/* Loading */}
        {loading && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '80px 0',
          }}>
            <div style={{
              width: 32, height: 32, borderRadius: '50%',
              border: '3px solid #E4E4E7',
              borderTopColor: '#E11D48',
              animation: 'spin 0.8s linear infinite',
            }} />
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={{
            backgroundColor: '#FEF2F2', border: '1px solid #FECACA',
            borderRadius: 4, padding: 20, color: '#DC2626',
            marginBottom: 24,
          }}>
            <p style={{ fontWeight: 600, marginBottom: 4 }}>Unable to load catalog</p>
            <p style={{ fontSize: 14 }}>{error}</p>
          </div>
        )}

        {/* Product Grid */}
        {!loading && !error && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
            gap: 20,
            paddingBottom: 80,
          }}>
            {filtered.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                isInProtocol={
                  protocolProductIds.ids.has(product.id) ||
                  protocolProductIds.names.has(product.name.toLowerCase())
                }
                onClick={() => navigate(`/catalog/${product.id}`)}
              />
            ))}
            {filtered.length === 0 && (
              <div style={{
                gridColumn: '1 / -1', textAlign: 'center',
                padding: '60px 20px', color: '#A1A1AA',
              }}>
                <p style={{ fontSize: 17, marginBottom: 8 }}>No supplements match your search.</p>
                <p style={{ fontSize: 14 }}>Try a different term or clear filters.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Catalog CTA footer */}
      {!loading && !error && (
        <div style={{
          backgroundColor: '#18181B', padding: '64px 24px',
          textAlign: 'center',
        }}>
          <h2 style={{
            fontFamily: "'Instrument Serif', Georgia, serif",
            fontSize: 'clamp(24px, 4vw, 36px)',
            fontWeight: 400, color: '#FFFFFF',
            marginBottom: 16,
          }}>
            Supplements should follow your bloodwork.
          </h2>
          <p style={{
            fontSize: 16, color: '#A1A1AA', maxWidth: 520,
            margin: '0 auto 28px', lineHeight: 1.6,
          }}>
            Upload your labs and the AI will map your biomarkers to the right supplements
            at the right doses. No guessing.
          </p>
          <Link
            to="/landing"
            style={{
              display: 'inline-block',
              padding: '16px 40px', fontSize: 15, fontWeight: 600,
              backgroundColor: '#E11D48', color: '#FFFFFF',
              border: 'none', borderRadius: 4,
              textDecoration: 'none',
              transition: 'all 0.25s',
            }}
          >
            Upload Your Labs
          </Link>
        </div>
      )}
    </div>
  );
}

// ── Product Card ──

function ProductCard({ product, isInProtocol, onClick }: { product: Product; isInProtocol: boolean; onClick: () => void }) {
  const [hovered, setHovered] = useState(false);

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex', flexDirection: 'column',
        backgroundColor: isInProtocol ? '#FFFBFC' : '#FFFFFF',
        border: `1px solid ${hovered ? '#D4D4D8' : isInProtocol ? '#FECDD3' : '#E4E4E7'}`,
        borderLeft: isInProtocol ? '3px solid #E11D48' : undefined,
        borderRadius: 4,
        padding: 24,
        cursor: 'pointer',
        textAlign: 'left',
        transition: 'all 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
        transform: hovered ? 'translateY(-3px)' : 'none',
        boxShadow: hovered ? '0 8px 32px rgba(0,0,0,0.06)' : '0 1px 2px rgba(0,0,0,0.04)',
        fontFamily: "'DM Sans', sans-serif",
        width: '100%',
      }}
    >
      {/* Top row: badges */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
        {isInProtocol && (
          <Badge label="In Your Protocol" className="bg-rose-50 text-rose-600 border-rose-200" />
        )}
        <Badge
          label={SUBCATEGORY_LABELS[product.subcategory] || product.subcategory}
          className={CATEGORY_COLORS[product.category] || 'bg-gray-50 text-gray-700 border-gray-200'}
        />
        {product.requiresRx && (
          <Badge label="Rx Required" className="bg-rose-50 text-rose-700 border-rose-200" />
        )}
      </div>

      {/* Name */}
      <h3 style={{
        fontFamily: "'Instrument Serif', Georgia, serif",
        fontSize: 22, fontWeight: 400, color: '#18181B',
        margin: '0 0 8px', lineHeight: 1.25,
      }}>
        {product.name}
      </h3>

      {/* Clinical notes / description preview */}
      <p style={{
        fontSize: 14, color: '#52525B', lineHeight: 1.55,
        margin: '0 0 16px',
        display: '-webkit-box',
        WebkitLineClamp: 3,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
      }}>
        {product.clinicalNotes || product.rationale || 'Targeted supplement mapped to specific biomarkers.'}
      </p>

      {/* Dosage */}
      <div style={{
        fontSize: 13, color: '#71717A', marginBottom: 16,
        fontFamily: "'JetBrains Mono', 'SF Mono', monospace",
      }}>
        {product.dosage}
      </div>

      {/* Price */}
      {product.pricing?.monthlyPrice && (
        <div style={{
          fontSize: 17, fontWeight: 700, color: '#18181B', marginBottom: 16,
        }}>
          {product.pricing.monthlyPrice}<span style={{ fontSize: 13, fontWeight: 400, color: '#71717A' }}>/mo</span>
        </div>
      )}

      {/* Bottom row: timing + evidence */}
      <div style={{
        display: 'flex', gap: 8, flexWrap: 'wrap',
        marginTop: 'auto',
      }}>
        <Badge
          label={TIMING_LABELS[product.timing] || product.timing}
          className={TIMING_COLORS[product.timing] || ''}
        />
        <Badge
          label={`${product.evidenceLevel.charAt(0).toUpperCase()}${product.evidenceLevel.slice(1)} evidence`}
          className={EVIDENCE_COLORS[product.evidenceLevel] || ''}
        />
      </div>

      {/* Target biomarkers preview */}
      {product.targetBiomarkers && product.targetBiomarkers.length > 0 && (
        <div style={{
          marginTop: 12, paddingTop: 12, borderTop: '1px solid #F4F4F5',
          display: 'flex', gap: 4, flexWrap: 'wrap',
        }}>
          {product.targetBiomarkers.slice(0, 3).map((b) => (
            <span key={b} style={{
              fontSize: 11, color: '#71717A', backgroundColor: '#F4F4F5',
              padding: '3px 8px', borderRadius: 3,
              fontFamily: "'JetBrains Mono', monospace",
            }}>
              {b}
            </span>
          ))}
          {product.targetBiomarkers.length > 3 && (
            <span style={{
              fontSize: 11, color: '#A1A1AA',
              padding: '3px 8px',
              fontFamily: "'JetBrains Mono', monospace",
            }}>
              +{product.targetBiomarkers.length - 3} more
            </span>
          )}
        </div>
      )}
    </button>
  );
}

// ── Badge ──

function Badge({ label, className }: { label: string; className: string }) {
  return (
    <span
      className={className}
      style={{
        display: 'inline-block',
        padding: '3px 10px',
        fontSize: 11,
        fontWeight: 600,
        borderRadius: 4,
        border: '1px solid',
        letterSpacing: 0.3,
        lineHeight: '18px',
      }}
    >
      {label}
    </span>
  );
}
